import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
export function FAQPage() {
  const faqs = [{
    question: 'Where is your service area?',
    answer: "Our expert maids are available in Bicester and Oxfordshire. However, we are expanding our team and the area we cover every day so if you are not sure try booking online or call us. We'll confirm in seconds."
  }, {
    question: 'How to book a home or commercial cleaning?',
    answer: "If you're online, simply select the service you want and click the Book Online button to get personalised pricing, schedule an appointment in real-time based on our actual availability, and receive a confirmation. It only takes 60 seconds. Use your smartphone, computer, or tablet. Alternatively, call us at 01869 601439 or 07432 285564, or email us at smithsandjengs@yahoo.com."
  }, {
    question: 'Do I need to be at home when you arrive for the cleaning?',
    answer: "That's entirely up to you! If you've got other things to do, just let us know how we can get access to your home. Some customers provide a garage or door code, others leave a hidden key outside. Let us know when you book online or when you call-in your request. Whatever works for you will work for us! Just let us know what method you're most comfortable with."
  }, {
    question: 'Do I need to supply the vacuum or cleaning products?',
    answer: "We come equipped with everything we need to make your home or commercial facility sparkle.\nBut if you'd prefer that we use your cleaning products, just let us know when you book online or by phone."
  }, {
    question: 'Do you use eco-friendly cleaning products?',
    answer: "Yes, we do! Part of our commitment as a member of the community is not to harm the environment. That's why we use only eco-friendly products which do just as good a cleaning job as traditional chemical cleaning products. If you prefer that we use traditional chemical products, just let us know when you book."
  }, {
    question: 'Do you clean homes that have pets?',
    answer: 'Yes, we do! When you set up your online booking, please let us know the name and species of your pet and anything we need to know to make sure our housekeeper know what to so while she or he is in your house.'
  }, {
    question: 'Will I receive reminders for single, recurring, or pre-paid bundle cleaning services?',
    answer: 'When you book online or call us, you can select to receive a text message or email before each scheduled service. We typically send reminders one day and two hours before each cleaning appointment.'
  }, {
    question: "What if I'm not happy with the work you do for me?",
    answer: "Our reputation is our most valuable asset, so we offer a 100% satisfaction guarantee. If you're not satisfied, please contact us and we'll issue a full refund or make it right with a do-over."
  }, {
    question: 'How long will it take to clean my home or commercial facility?',
    answer: "Our time on-site will be based on the level of cleaning you select and the extras (if any) you order. We ensure and guarantee the quality of our service. If you aren't satisfied, we'll come back and make sure to address your concerns. 99% of our customers are repeat customers."
  }, {
    question: 'How do I re-schedule or cancel a booked cleaning?',
    answer: "Rescheduling or cancelling is one click away. Or simply call us or email us and we'll update your booking. It just takes a few seconds and we'll send you an updated booking confirmation. Our email service reminders can include a Reschedule and/or Cancel button which allows you to instantly reschedule or cancel your booking within pre-established periods prior to each service."
  }, {
    question: 'Can I set up repeat visits?',
    answer: "Yes, and you'll save when you do. For each of our services you can select and schedule either recurring appointments or purchase pre-paid bundles and save instantly. Check out our home cleaning savings on our Live Pricing & Real-Time Booking Pages."
  }, {
    question: 'How can I pay you?',
    answer: 'We accept VISA, Mastercard, American Express, and Discover through our convenient Live Pricing & Real-Time Booking Pages. Book online or call us. We also accept cash or cheque at the time of service.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  useEffect(() => {
    // FAQ structured data for rich snippets (SEO/AEO)
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ld-faq';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    });
    document.head.appendChild(script);
    // Breadcrumb schema
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'ld-breadcrumb-faq';
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: window.location.origin + '/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'FAQ',
        item: window.location.origin + '/faq'
      }]
    });
    document.head.appendChild(breadcrumbScript);
    return () => {
      document.getElementById('ld-faq')?.remove();
      document.getElementById('ld-breadcrumb-faq')?.remove();
    };
  }, []);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <main className="min-h-screen bg-light pb-24" data-id="element-1253">
      <SEOHead title="FAQ | Smith & Jengs Cleaning Services" description="Find answers to common questions about our cleaning services in Bicester. Service areas, booking, eco-friendly products, and more." data-id="element-1254" />
      {/* Dark Page Hero */}
      <section className="bg-primary pt-36 md:pt-44 pb-16 md:pb-24 relative overflow-hidden" data-id="element-1255">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.15) 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-1256"></div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center" data-id="element-1257">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1258">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-white uppercase leading-none tracking-tight" data-id="element-1259">
              Frequently Asked Questions
            </h1>
          </motion.div>
        </div>
      </section>

      {/* FAQ Grid */}
      <section className="container mx-auto px-6 md:px-12 py-16 md:py-24 max-w-7xl" data-id="element-1260">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1261">
          {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.04
          }} layout className={`rounded-2xl overflow-hidden cursor-pointer transition-colors duration-300 ${isOpen ? 'bg-primary text-white shadow-xl' : 'bg-white text-primary shadow-md hover:shadow-lg border border-gray-100'}`} onClick={() => toggleFAQ(index)} data-id="element-1262">
                <div className="p-6" data-id="element-1263">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 mb-3" data-id="element-1264">
                    <span className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold ${isOpen ? 'bg-accent text-white' : 'bg-accent/10 text-accent'}`} data-id="element-1265">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors ${isOpen ? 'bg-white/15 text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-1266">
                      {isOpen ? <MinusIcon size={14} data-id="element-1267" /> : <PlusIcon size={14} data-id="element-1268" />}
                    </div>
                  </div>

                  {/* Question */}
                  <h3 className={`font-semibold leading-snug ${isOpen ? 'text-white' : 'text-primary'}`} data-id="element-1269">
                    {faq.question}
                  </h3>

                  {/* Answer */}
                  <AnimatePresence data-id="element-1270">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-1271">
                        <div className="pt-4 mt-4 border-t border-white/15 text-white/80 text-sm leading-relaxed whitespace-pre-line" data-id="element-1272">
                          {faq.answer}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </motion.div>;
        })}
        </div>
      </section>
    </main>;
}